import { categories, fragrances, colors } from "@/lib/products";

export type Filters = {
  category: string | null;
  price: number;
  fragrances: string[];
  color: string | null;
  inStockOnly: boolean;
  collection?: string | null;
};

type Props = {
  filters: Filters;
  onChange: (patch: Partial<Filters>) => void;
  onReset: () => void;
};

export function FilterSidebar({ filters, onChange, onReset }: Props) {
  return (
    <aside className="lg:sticky lg:top-28 lg:self-start bg-card rounded-[20px] shadow-luxe p-7 space-y-8">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-semibold text-primary">Refine</h3>
        <button onClick={onReset} className="text-[0.7rem] tracking-[0.2em] uppercase text-gold hover:text-primary font-semibold transition-colors cursor-pointer">
          Reset
        </button>
      </div>

      <Group title="Category">
        <button
          onClick={() => onChange({ category: null, collection: null })}
          className={`block w-full text-left text-sm py-1.5 transition-colors cursor-pointer ${filters.category === null && !filters.collection ? "text-gold font-semibold" : "text-muted-foreground hover:text-primary"}`}
        >
          All Candles
        </button>
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => onChange({ category: c, collection: null })}
            className={`block w-full text-left text-sm py-1.5 transition-colors cursor-pointer ${filters.category === c ? "text-gold font-semibold" : "text-muted-foreground hover:text-primary"}`}
          >
            {c} Collection
          </button>
        ))}
      </Group>

      <Group title="Price">
        <input
          type="range"
          min={299}
          max={2000}
          step={50}
          value={filters.price}
          onChange={(e) => onChange({ price: Number(e.target.value) })}
          className="w-full accent-[color:var(--gold)]"
        />
        <div className="mt-2 flex justify-between text-xs text-muted-foreground">
          <span>₹299</span>
          <span className="text-primary font-medium">Up to ₹{filters.price}</span>
        </div>
      </Group>

      <Group title="Fragrance">
        <div className="space-y-2">
          {fragrances.map((f) => {
            const checked = filters.fragrances.includes(f);
            return (
              <label key={f} className="flex items-center gap-3 text-sm cursor-pointer group">
                <span
                  className={`h-4 w-4 rounded-[4px] border grid place-items-center transition-colors ${checked ? "bg-gold border-gold" : "border-border group-hover:border-gold"}`}
                >
                  {checked && <span className="h-1.5 w-1.5 bg-primary rounded-[1px]" />}
                </span>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={checked}
                  onChange={() => {
                    const next = checked ? filters.fragrances.filter((x) => x !== f) : [...filters.fragrances, f];
                    onChange({ fragrances: next });
                  }}
                />
                <span className={checked ? "text-primary" : "text-muted-foreground"}>{f}</span>
              </label>
            );
          })}
        </div>
      </Group>

      <Group title="Color">
        <div className="flex gap-3">
          {colors.map((c) => {
            const active = filters.color === c.key;
            return (
              <button
                key={c.key}
                onClick={() => onChange({ color: active ? null : c.key })}
                aria-label={c.key}
                className={`h-8 w-8 rounded-full ring-offset-2 ring-offset-card transition-all ${active ? "ring-2 ring-gold" : "hover:ring-1 hover:ring-gold/50"}`}
                style={{ background: c.hex }}
              />
            );
          })}
        </div>
      </Group>

      <Group title="Availability">
        <label className="flex items-center gap-3 text-sm cursor-pointer">
          <span
            className={`h-4 w-4 rounded-[4px] border grid place-items-center transition-colors ${filters.inStockOnly ? "bg-gold border-gold" : "border-border"}`}
          >
            {filters.inStockOnly && <span className="h-1.5 w-1.5 bg-primary rounded-[1px]" />}
          </span>
          <input type="checkbox" className="sr-only" checked={filters.inStockOnly} onChange={(e) => onChange({ inStockOnly: e.target.checked })} />
          <span className={filters.inStockOnly ? "text-primary" : "text-muted-foreground"}>In stock only</span>
        </label>
      </Group>
    </aside>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-[0.7rem] tracking-[0.28em] uppercase text-primary font-medium mb-4">{title}</h4>
      <div>{children}</div>
    </div>
  );
}
